declare const __PLATFORM_SERVER_BASE_URL__: string;

import { useState, useLayoutEffect, useEffect, useCallback } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router';
import { Moon, Sun } from '@phosphor-icons/react';
import { Header, AISearch } from '@boomi/magnetosphere';
import { ExIcon, ExLeftmenubarAdjustable, ExLeftmenubarLink } from '@boomi/exosphere';
import { appRoutes } from '../routes';
import styles from './AppShell.module.css';
import { getAuthFromWindow } from '../authentication/authUtils';

const getIsDarkTheme = () => {
    const stored = localStorage.getItem('theme');
    if (stored) return stored === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

const getIsMenuCollapsed = () => localStorage.getItem('menuCollapsed') === 'true';

const platformUrl = new URL(__PLATFORM_SERVER_BASE_URL__).hostname;

const removeCookie = (name: string) => {
    document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; secure;`;
};

const pageInfo = {
    name: 'Connect',
    homePage: { url: '/', title: 'Connect' },
    redirect: { url: '/', title: 'Connect' },
};

const AppShell = () => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [isDark, setIsDark] = useState(getIsDarkTheme);
    const [isMenuCollapsed] = useState(getIsMenuCollapsed);
    const [jwt, setJwt] = useState('');
    const [isAiSearchOpen, setIsAiSearchOpen] = useState(false);

    useEffect(() => {
        const auth = getAuthFromWindow();
        if (!auth) return;

        auth.getToken().then((token: string) => setJwt(token));

        auth.getAccountId().then((accountId: string) => {
            document.cookie = `ACCOUNT_ID=${accountId}; path=/; secure;`;
        });
    }, []);

    useLayoutEffect(() => {
        if (isDark) {
            document.documentElement.classList.add('ex-theme-dark');
            document.documentElement.classList.remove('ex-theme-light');
        } else {
            document.documentElement.classList.add('ex-theme-light');
            document.documentElement.classList.remove('ex-theme-dark');
        }
    }, [isDark]);

    const toggleTheme = () => {
        setIsDark((prev) => {
            const next = !prev;
            localStorage.setItem('theme', next ? 'dark' : 'light');
            return next;
        });
    };

    const handleSignOut = useCallback(async () => {
        removeCookie('ACCOUNT_ID');
        try {
            await getAuthFromWindow()?.logout();
        } catch {
            // continue to login even if server logout fails
        }
        navigate('/');
    }, [navigate]);

    const handleSwitchAccountSuccess = useCallback(async ({ accountId }: { accountId: string }) => {
        removeCookie('ACCOUNT_ID');
        const auth = getAuthFromWindow();
        await auth?.logout();
        await auth?.getToken(accountId);
        window.location.reload();
    }, []);

    const handleAuthError = useCallback(() => {
        removeCookie('ACCOUNT_ID');
        navigate('/');
    }, [navigate]);

    return (
        <div className={styles.shell}>
            <Header
                pageInfo={pageInfo}
                checkForDevelopment={process.env['NODE_ENV'] === 'development'}
                platformUrl={platformUrl}
                showBoomiAi={() => setIsAiSearchOpen(true)}
                searchIconEventHandler={() => setIsAiSearchOpen((prev) => !prev)}
                onSignOutSuccess={handleSignOut}
                onSwitchAccountSuccess={handleSwitchAccountSuccess}
                onAuthError={handleAuthError}
                disableTranslation
                suppressDefaultLandingPage
                noNeedAuthorization
                jwt={jwt}
            />
            <AISearch
                openSearchPanel={isAiSearchOpen}
                userIsAuthenticated
                closeBtnEventHandler={() => setIsAiSearchOpen(false)}
                jwt={jwt}
            />
            <div className={styles.content}>
                <ExLeftmenubarAdjustable
                    isCollapse={isMenuCollapsed}
                    adjustable
                    animated
                    expandWidth={264}
                    collapseWidth={56}
                    // Detail is nested in another detail for some reason, so we have to do (ev.detail as any).detail.collapse to get the value
                    onCollapse={(ev) =>
                        localStorage.setItem(
                            'menuCollapsed',
                            String((ev.detail as any).detail.collapse),
                        )
                    }
                >
                    {appRoutes.map(({ path, label, exIcon }) => (
                        <ExLeftmenubarLink
                            key={path}
                            label={label}
                            tooltipText={label}
                            selected={pathname.startsWith(`/${path}`)}
                            onItemSelection={() => navigate(`/${path}`)}
                        >
                            <ExIcon slot="icon" icon={exIcon} />
                        </ExLeftmenubarLink>
                    ))}
                </ExLeftmenubarAdjustable>
                <div className={styles.mainArea}>
                    <div className={styles.topBar}>
                        <button
                            className={styles.themeToggle}
                            onClick={toggleTheme}
                            aria-label="Toggle theme"
                        >
                            {isDark ? (
                                <Sun size={16} weight="duotone" />
                            ) : (
                                <Moon size={16} weight="duotone" />
                            )}
                        </button>
                    </div>
                    <main className={styles.main}>
                        <Outlet />
                    </main>
                </div>
            </div>
        </div>
    );
};

export default AppShell;
